import React, { useMemo } from 'react';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import SideDrawerFormContainer, {
    ConfirmDirtyStateFormCloseContainer,
    SideDrawerDirtyFormExitValidationTracker,
} from '../../../../../../v2BaseComponents/Form/SideDrawerFormContainer';
import usePersistVisitToClientProfiles from '../../../../../../../hooks/api/usePersistVisitToClientProfiles';
import {
    VisitFormFields,
    getDefaultFormValue,
    handleVisitFormSubmit,
} from '../../../../../../shared/ResourceForms/VisitForm/Form';
import RenderWithProviders from '../../../../../../shared/ResourceForms/RenderWithProviders';
import { Form } from '../../../../../../v2BaseComponents/Form';
import { handleFormikSubmit } from '../../../../../../../utils/handleFormikSubmit';
import { APITypes, MedicalDataTypes } from '../../../../../../../primary-record-types';

export type CopyVisitToClientsSideDrawerContentProps = {
    clientProfileIds: string[];
    onClose: (force: boolean) => void;
};

const CopyVisitToClientsSideDrawerContent: React.FC<CopyVisitToClientsSideDrawerContentProps> = ({
    clientProfileIds,
    onClose,
}) => {
    // A single selected client has a concrete care team to offer as the Provider option;
    // with multiple clients selected there is no shared care team, so none is loaded.
    if (clientProfileIds.length === 1) {
        return (
            <RenderWithProviders
                medicalProfileId={clientProfileIds[0]}
                includeSupportCareTeamMembers
                render={(providers) => (
                    <CopyVisitToClientsSideDrawerForm
                        clientProfileIds={clientProfileIds}
                        onClose={onClose}
                        providers={providers}
                    />
                )}
            />
        );
    }
    return <CopyVisitToClientsSideDrawerForm clientProfileIds={clientProfileIds} onClose={onClose} providers={[]} />;
};

const CopyVisitToClientsSideDrawerForm: React.FC<
    CopyVisitToClientsSideDrawerContentProps & { providers: React.ComponentProps<typeof VisitFormFields>['providers'] }
> = ({ clientProfileIds, onClose, providers }) => {
    const { isPersisting, persistVisitToClientProfiles } = usePersistVisitToClientProfiles({
        onPersistSuccess: () => onClose(true),
    });

    const defaultFormValue = useMemo(() => getDefaultFormValue(false), []);

    return (
        <AnimateLoadedContent isLoading={isPersisting}>
            {() => (
                <ConfirmDirtyStateFormCloseContainer>
                    <Form
                        initialValues={defaultFormValue}
                        onSubmit={(values, helpers) => {
                            const data = handleVisitFormSubmit(values, providers);
                            // A recurrence rule expands server-side, one series per client.
                            persistVisitToClientProfiles({
                                resource: data.resourceUpdate as Omit<MedicalDataTypes.PrimaryRecordVisit, 'id'>,
                                images: data.imageUpdates.filter(
                                    (_) => _.type === 'create',
                                ) as APITypes.medicalDataService.ResourceImageCreate[],
                                destinationMedicalProfileIds: clientProfileIds,
                                sourceNoteId: data.sourceNoteId,
                            });
                            handleFormikSubmit(values, helpers);
                        }}
                        isDisabled={isPersisting}
                        onCancel={() => onClose(true)}
                        fillContainer
                        forceStackFields
                    >
                        {({ isSubmitting, handleSubmit, setFieldValue, values }) => (
                            <SideDrawerFormContainer
                                isDisabled={isPersisting || isSubmitting}
                                onSubmit={handleSubmit}
                                onCancel={() => onClose(true)}
                            >
                                <SideDrawerDirtyFormExitValidationTracker />
                                <VisitFormFields
                                    isDisabled={isPersisting}
                                    isLoading={isPersisting}
                                    values={values}
                                    providers={providers}
                                    profileId=""
                                    setFieldValue={setFieldValue}
                                    showOrganizationUserSelect
                                    autoOpenWhenPicker
                                    showNotePicker
                                    showRecurrenceControls
                                />
                            </SideDrawerFormContainer>
                        )}
                    </Form>
                </ConfirmDirtyStateFormCloseContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(CopyVisitToClientsSideDrawerContent);
